// FirstRunChecklist — shown to admins and managers of brand-new packhouse
// tenants to nudge them through the master-data setup that the platform-admin
// Phase A wizard intentionally leaves empty (per the user's "Empty +
// in-app builder" install model). One row per registry that needs at
// least one record before the packhouse can run a full day's flow.
//
// Rules:
//   * Admins and managers (isManagerOrAbove) — operators don't need
//     to see setup nags. A step only an admin can do (rate cards — the
//     financial_products write policies are ADMIN-only — and inviting the
//     team) says "An admin does this" to a manager instead of a link.
//   * A business's FIRST DAYS only: hidden once it has saved a pallet, so a
//     live business never gets the card back (F19 UF-01). A failed pallet
//     read hides it too.
//   * Once every required item is done it shows ONE "You're set up" Done panel
//     (first record + invite the team), dismissible and remembered per tenant on
//     this device (`kf49.setupDone.dismissed.<tenant>`), then renders nothing.
//     The setup CARD still has no dismiss — the only
//     ways to make it disappear are to finish the setup, mark a step "Not
//     needed here" (business-wide), or save the first pallet. (Otherwise pilot
//     customers tick it away and forget, then can't dispatch on day 5.) F19
//     UF-29 removed the per-PC "Mark setup complete" button that broke this
//     rule; any `kf49.firstRunChecklist.dismissed.<tenant>` key a browser
//     still holds is read by nothing.
//   * Each item reads whether the step is REALLY done (firstRunChecks.ts —
//     D1: identity, markets and team used to tick on day one). A read that
//     fails shows "Couldn't check", never a tick and never a zero.
//   * Deep-links to the existing admin surfaces — no duplicated UI. A Config
//     step opens its exact section (packhouseConfigHref → ?section=), and
//     Config's Back returns here (useNavigateWithReturn / useReturnTo). A
//     Config step and "Open setup" are offered only to someone /config admits
//     (useCanOpenPackhouseConfig); a role it would bounce to Home gets no link.
//   * Lists only the steps still OPEN (a step marked "Not needed here" on
//     Config → Setup — UF-30, business-wide — is not); "Open setup" goes to Config → Setup,
//     where the same rows (SetupSteps.tsx, one hook for both) show with their
//     ticks above or below the setup checks.

import { Sparkles, ChevronRight } from 'lucide-react'
import { useNavigateWithReturn } from '@/lib/returnTo'
import { packhouseConfigHref } from '@/lib/packhouseConfigHref'
import { useSetupSteps, SetupStepList, stepSettled } from './SetupSteps'
import SavedBanner, { SavedBannerLink } from './SavedBanner'
import { useTenantPersistedState } from '@/hooks/useTenantPersistedState'

/** Setup is complete: say so once, with the next jobs, then stay hidden. */
function SetupDonePanel({ admin }: { admin: boolean }) {
  const [dismissed, setDismissed] = useTenantPersistedState('kf49.setupDone.dismissed', false)
  if (dismissed) return null
  return (
    <SavedBanner className="mb-4" onDismiss={() => setDismissed(true)}
      title="You're set up"
      actions={<>
        <SavedBannerLink to="/deliveries">Record your first delivery</SavedBannerLink>
        {admin && <SavedBannerLink to="/users">Invite your team</SavedBannerLink>}
      </>}>
      <p>All required steps are done.</p>
    </SavedBanner>
  )
}

export default function FirstRunChecklist() {
  const navigate = useNavigateWithReturn()
  const {
    canSee, admin, canOpenConfig, firstDays, rows, doneCount, totalCount, unknownCount, isLoading, allDone,
  } = useSetupSteps({ firstDaysOnly: true })

  // A business's first days only, and only once we KNOW it has no pallet: a
  // pending or failed pallet read hides the card rather than showing it.
  if (!canSee || firstDays !== true) return null
  if (isLoading) return null
  if (allDone) return <SetupDonePanel admin={admin} />

  // Home lists only what is still open; the whole list, ticks included,
  // lives on Config → Setup ("Open setup").
  // A step the business marked "Not needed here" (Config → Setup) is settled
  // on every PC, so it leaves this list too.
  const open = rows.filter(r => !stepSettled(r))

  return (
    <section className="card border-emerald-700/40 bg-emerald-900/10 p-4 mb-4">
      <header className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-emerald-900/40 flex items-center justify-center shrink-0">
          <Sparkles size={18} className="text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-emerald-100">Finish setting up your packhouse</h2>
          <p className="text-xs text-emerald-200/70 mt-0.5">
            {doneCount} of {totalCount} required steps complete. Shown until your first pallet. Each step opens where you fill it in.
            {unknownCount > 0 && (
              <span className="text-amber-300">
                {' '}{unknownCount === 1 ? '1 step' : `${unknownCount} steps`} couldn't be checked.
              </span>
            )}
          </p>
        </div>
        <span className="text-xs font-mono text-emerald-300 shrink-0">
          {Math.round((doneCount / totalCount) * 100)}%
        </span>
      </header>

      <div className="mb-2">
        <SetupStepList rows={open} admin={admin} canOpenConfig={canOpenConfig}
          onOpen={to => navigate(to)} />
      </div>
      {/* /config bounces anyone it doesn't admit to Home (a custom
          MANAGER-based role without the config module): offer it only to
          those it admits, and not while that is still deciding (Jason, 3 Oct). */}
      {canOpenConfig === true && (
        <button onClick={() => navigate(packhouseConfigHref('health', 'Getting started'))}
          className="tap-target-y w-full text-left px-2 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-900/30">
          <span className="flex-1">Open setup</span>
          <ChevronRight size={14} className="shrink-0" />
        </button>
      )}
    </section>
  )
}
